import { createFileRoute, Link } from "@tanstack/react-router";
import { Minus, Plus, Trash2, ShoppingBag, Truck, ShieldCheck, RotateCcw, ArrowRight } from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useCart, selectSubtotalCents, freeShippingRemainingCents } from "@/lib/cart/cart-store";
import { useShippingConfig, formatEuro, headlineFreeOver } from "@/hooks/useShippingConfig";
import { RETURNS_DAYS_DEFAULT } from "@/lib/site-config";
import { trackCheckout } from "@/lib/client-errors/tracker";
import { trackBeginCheckout, trackViewCart } from "@/lib/analytics/tracker";
import { lazy, Suspense, useEffect } from "react";

const ExpressCheckout = lazy(() =>
  import("@/components/checkout/ExpressCheckout").then((m) => ({ default: m.ExpressCheckout })),
);

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your cart — FIXHEN" },
      { name: "description", content: "Review parts in your FIXHEN cart before checkout." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const items = useCart((s) => s.items);
  const subtotal = useCart(selectSubtotalCents);
  const updateQty = useCart((s) => s.updateQty);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const { data: shipping } = useShippingConfig();
  const threshold = headlineFreeOver(shipping?.zones);
  const remaining = freeShippingRemainingCents(subtotal, threshold);
  const pct = threshold ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 0;

  useEffect(() => {
    trackCheckout("viewed_cart", { items: items.length, subtotal_cents: subtotal });
    trackViewCart({ items: items.length, subtotalEur: subtotal / 100 });
  }, []);

  return (
    <div>
      <AnnouncementBar />
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-extrabold text-[#172554] mb-6">Shopping cart</h1>
        {items.length === 0 ? (
          <div className="rounded-2xl border bg-white p-12 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-slate-100 text-slate-400">
              <ShoppingBag className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-lg font-bold text-[#172554]">Your cart is empty</h2>
            <p className="mt-1 text-sm text-slate-500">Browse the shop to find OEM-matched parts for your car.</p>
            <Link to={"/shop" as never} className="mt-5 inline-flex h-11 items-center rounded-md bg-[#172554] px-5 font-semibold text-white hover:bg-royal">
              Go to shop <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[1fr_360px]">
            <div className="rounded-2xl border bg-white overflow-hidden">
              <ul className="divide-y">
                {items.map((it) => (
                  <li key={it.key} className="flex gap-4 p-4">
                    <Link to="/p/$slug" params={{ slug: it.slug }} className="h-24 w-24 shrink-0 overflow-hidden rounded-md border bg-white">
                      {it.image ? <img src={it.image} alt={it.title} className="h-full w-full object-contain p-1.5" /> : null}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to="/p/$slug" params={{ slug: it.slug }} className="line-clamp-2 text-sm font-semibold text-[#172554] hover:text-royal">
                        {it.title}
                      </Link>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {it.brand ? <>{it.brand} · </> : null}
                        <span className="font-mono">{it.sku}</span>
                        {it.variantLabel ? <> · {it.variantLabel}</> : null}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center rounded-md border">
                          <button aria-label="Decrease" onClick={() => updateQty(it.key, it.qty - 1)} className="grid h-8 w-8 place-items-center hover:bg-slate-50">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-8 px-2 text-center text-sm font-bold tabular-nums">{it.qty}</span>
                          <button aria-label="Increase" onClick={() => updateQty(it.key, it.qty + 1)} disabled={it.maxQty != null && it.qty >= it.maxQty} className="grid h-8 w-8 place-items-center hover:bg-slate-50 disabled:opacity-40">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-base font-bold text-[#172554] tabular-nums">{formatEuro(it.unitPriceCents * it.qty)}</span>
                          <button aria-label="Remove" onClick={() => remove(it.key)} className="text-slate-400 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t px-4 py-3 text-xs">
                <Link to={"/shop" as never} className="font-semibold text-royal hover:underline">← Continue shopping</Link>
                <button onClick={() => clear()} className="text-slate-500 hover:text-rose-600">Empty cart</button>
              </div>
            </div>

            <aside className="space-y-3">
              <div className="rounded-2xl border bg-white p-5">
                <h2 className="text-sm font-bold text-[#172554]">Order summary</h2>
                {threshold != null && (
                  <div className="mt-3 rounded-lg bg-sky-pop/15 p-3 text-xs">
                    {remaining === 0 ? (
                      <span className="font-semibold text-emerald-700">Free worldwide shipping unlocked.</span>
                    ) : (
                      <>
                        <span>Add <b>{formatEuro(remaining ?? 0)}</b> more for free shipping.</span>
                        <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                          <div className="h-full rounded-full bg-royal transition-[width] duration-500" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                )}
                <dl className="mt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-600">Subtotal</dt><dd className="font-semibold tabular-nums">{formatEuro(subtotal)}</dd></div>
                  <div className="flex justify-between text-xs text-slate-500"><dt>Shipping</dt><dd>Calculated at checkout</dd></div>
                  <div className="flex justify-between border-t pt-2 text-base"><dt className="font-bold text-[#172554]">Total (VAT incl.)</dt><dd className="font-extrabold text-[#172554] tabular-nums">{formatEuro(subtotal)}</dd></div>
                </dl>
                <Link to={"/checkout" as never} onClick={() => {
                  trackCheckout("proceed_click", { subtotal_cents: subtotal, items: items.length });
                  trackBeginCheckout({ items: items.length, subtotalEur: subtotal / 100, method: "standard" });
                }} className="mt-4 flex h-11 items-center justify-center rounded-md bg-[#172554] font-semibold text-white hover:bg-royal">
                  Proceed to checkout
                </Link>

                <Suspense fallback={null}>
                  <div className="mt-3">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      <span className="h-px flex-1 bg-slate-200" />
                      or pay instantly
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>
                    <ExpressCheckout
                      items={items.map((it) => ({
                        productId: it.productId,
                        variantId: it.variantId,
                        qty: it.qty,
                        title: it.title,
                        unitPriceCents: it.unitPriceCents,
                      }))}
                      subtotalCents={subtotal}
                    />
                  </div>
                </Suspense>
              </div>
              <div className="rounded-2xl border bg-white p-5 text-xs text-slate-600 space-y-2">
                <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-royal" /> Worldwide shipping · free over {threshold ? formatEuro(threshold) : "€99"}</div>
                <div className="flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5 text-royal" /> {RETURNS_DAYS_DEFAULT}-day free returns, no questions asked</div>
                <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-royal" /> Fitment guarantee on every OEM-matched part.</div>
              </div>
            </aside>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}